import { useMemo } from "react";
import { useTenant } from "hooks/settings";
import { useParameterFeasibility } from "hooks/useParameterFeasibility";
import {
  CheckParameterFeasibilityRequest,
  useGetChefGroupingQuery,
  useGetMaterialLimitSetQuery,
} from "store/api/generatedApi";
import { useLimitLabelBySignature } from "./useLimitLabelBySignature";

export const useMaterialPropertiesFeasibility = ({
  isStale,
  candidate,
}: {
  isStale: boolean;
  candidate: Pick<
    CheckParameterFeasibilityRequest,
    "material_chemistry_overrides" | "material_physics_overrides"
  >;
}) => {
  const tenantName = useTenant();
  const grouping = useGetChefGroupingQuery({
    tenantName,
    chefGroupingId: "default",
  });
  const materialLimits = useGetMaterialLimitSetQuery({
    tenantName,
    materialLimitSetId: "default",
    source: "customer",
  });
  const request = useMemo<CheckParameterFeasibilityRequest | null>(
    () =>
      isStale || !grouping.currentData || !materialLimits.currentData
        ? null
        : {
            ...candidate,
            material_limits: materialLimits.currentData.material_limits,
            target_baskets: grouping.currentData.chef_groups.flatMap((group) =>
              group.id == null
                ? []
                : [
                    {
                      chef_group_id: group.id,
                      target_num_baskets: group.default_target_num_baskets,
                    },
                  ]
            ),
          },
    [candidate, isStale, grouping.currentData, materialLimits.currentData]
  );
  const feasibility = useParameterFeasibility(request);
  const chefGroupNameById = useMemo(
    () =>
      Object.fromEntries(
        (grouping.currentData?.chef_groups ?? []).flatMap((group) =>
          group.id == null ? [] : [[group.id, group.name] as const]
        )
      ),
    [grouping.currentData]
  );
  const limitLabelBySignature = useLimitLabelBySignature();
  return {
    ...feasibility,
    request,
    chefGroupNameById,
    limitLabelBySignature,
    hasError:
      feasibility.hasError || grouping.isError || materialLimits.isError,
  };
};
